"use client";

import { useState, useEffect, useMemo, useRef } from "react";
import { TierList } from "@/types/tier-list";
import {
  getAllTierLists,
  deleteTierList,
  saveTierList,
  resolveImage,
  setLastUsedTierListId,
} from "@/lib/indexdb";
import { exportTierListTemplate, downloadTemplate } from "@/lib/template-utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Settings,
  Plus,
  Pencil,
  Trash2,
  Download,
  Presentation,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { TierListCreationWizard } from "@/components/tier-list-creation-wizard";
import { EditTierListForm } from "@/components/edit-tier-list-form";

interface TierListHeaderProps {
  tierList: TierList;
  onTierListChange: (tierListId: string) => void;
  isPresentationMode: boolean;
  onTogglePresentationMode: () => void;
}

// Utility to calculate optimal font size for title
function calculateTitleFontSize(
  text: string,
  maxWidth: number,
  startFontSize: number = 32,
  minFontSize: number = 18
): number {
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  if (!context) return minFontSize;

  const fontFamily =
    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

  for (let fontSize = startFontSize; fontSize >= minFontSize; fontSize -= 2) {
    context.font = `bold ${fontSize}px ${fontFamily}`;
    const metrics = context.measureText(text);

    if (metrics.width <= maxWidth) {
      return fontSize;
    }
  }

  return minFontSize;
}

export function TierListHeader({
  tierList,
  onTierListChange,
  isPresentationMode,
  onTogglePresentationMode,
}: TierListHeaderProps) {
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [isEditTitleOpen, setIsEditTitleOpen] = useState(false);
  const [titleEditValue, setTitleEditValue] = useState(tierList.name);
  const [titleError, setTitleError] = useState<string>("");
  const [allTierLists, setAllTierLists] = useState<TierList[]>([]);
  const [isManageOpen, setIsManageOpen] = useState(false);
  const [editingTierListId, setEditingTierListId] = useState<string | null>(
    null
  );
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [tierListToDelete, setTierListToDelete] = useState<TierList | null>(
    null
  );
  const [deleteError, setDeleteError] = useState<string>("");
  const [exportError, setExportError] = useState<string>("");
  const titleContainerRef = useRef<HTMLDivElement>(null);

  const MAX_TITLE_LENGTH = 100;

  // Load logo
  useEffect(() => {
    let objectUrl: string | null = null;

    const loadLogo = async () => {
      if (tierList.logoId) {
        objectUrl = await resolveImage(tierList.logoId);
        setLogoUrl(objectUrl);
      } else {
        setLogoUrl(null);
      }
    };
    loadLogo();

    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [tierList.logoId]);

  // Load all tier lists for management
  const loadTierLists = async () => {
    const lists = await getAllTierLists();
    setAllTierLists(lists);
  };

  useEffect(() => {
    loadTierLists();
  }, []);

  // Sync with IndexedDB whenever the gear dropdown opens
  useEffect(() => {
    if (isManageOpen) {
      loadTierLists();
    }
  }, [isManageOpen]);

  // Calculate dynamic font size for title
  const titleFontSize = useMemo(() => {
    if (!titleContainerRef.current) return 32;
    const maxWidth = titleContainerRef.current.offsetWidth - 32; // Subtract padding
    return calculateTitleFontSize(tierList.name, maxWidth);
  }, [tierList.name]);

  const handleTitleClick = () => {
    setTitleEditValue(tierList.name);
    setTitleError("");
    setIsEditTitleOpen(true);
  };

  const handleTitleSave = async () => {
    const trimmed = titleEditValue.trim();

    if (!trimmed) {
      setTitleError("Title cannot be empty");
      return;
    }

    if (trimmed.length > MAX_TITLE_LENGTH) {
      setTitleError(`Title must be ${MAX_TITLE_LENGTH} characters or less`);
      return;
    }

    if (trimmed !== tierList.name) {
      const updatedTierList = {
        ...tierList,
        name: trimmed,
      };
      await saveTierList(updatedTierList);
      // Reload tier lists to sync with IndexedDB
      await loadTierLists();
      // Trigger refresh by calling onTierListChange with current id
      onTierListChange(tierList.id);
    }

    setIsEditTitleOpen(false);
    setTitleError("");
  };

  const handleTitleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleTitleSave();
    }
  };

  const handleDeleteTierList = (id: string) => {
    const listToDelete = allTierLists.find((list) => list.id === id);
    if (listToDelete) {
      setTierListToDelete(listToDelete);
      setDeleteError(""); // Clear any previous errors
      setDeleteConfirmOpen(true);
    }
  };

  const confirmDelete = async () => {
    if (!tierListToDelete || deletingId) return;

    setDeletingId(tierListToDelete.id);
    setDeleteConfirmOpen(false);

    try {
      await deleteTierList(tierListToDelete.id);
      await loadTierLists();

      // If we deleted the current tier list, switch to another one
      if (tierListToDelete.id === tierList.id) {
        const remaining = await getAllTierLists();
        if (remaining.length > 0) {
          await setLastUsedTierListId(remaining[0].id);
          onTierListChange(remaining[0].id);
        } else {
          // No tier lists left, redirect to home or show create form
          window.location.href = "/";
        }
      }
    } catch (error) {
      console.error("Failed to delete tier list:", error);
      setDeleteError("Failed to delete tier list. Please try again.");
      setDeleteConfirmOpen(true); // Reopen dialog to show error
    } finally {
      setDeletingId(null);
    }
  };

  const handleSwitchTierList = async (id: string) => {
    await setLastUsedTierListId(id);
    onTierListChange(id);
    setIsManageOpen(false);
  };

  const handleEditTierList = (id: string) => {
    setEditingTierListId(id);
    setIsManageOpen(false);
  };

  const handleCreateSuccess = async (tierListId: string) => {
    setIsCreateOpen(false);
    // Reload tier lists to include the newly created one
    await loadTierLists();
    await setLastUsedTierListId(tierListId);
    onTierListChange(tierListId);
  };

  const handleEditSuccess = async (tierListId: string) => {
    setEditingTierListId(null);
    await loadTierLists();
    // Refresh current tier list if it was edited
    if (tierListId === tierList.id) {
      onTierListChange(tierListId);
    }
  };

  const handleExportTemplate = async () => {
    setExportError(""); // Clear previous errors
    try {
      const template = await exportTierListTemplate(tierList);
      downloadTemplate(template);
      setIsManageOpen(false);
    } catch (error) {
      console.error("Failed to export template:", error);
      setExportError("Failed to export template. Please try again.");
    }
  };

  return (
    <>
      <div className="h-32 bg-card border-b border-border flex items-center px-6 gap-6">
        <div className="max-w-40 h-full py-2 flex items-center justify-center">
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={tierList.name}
              className="h-full w-40 object-contain"
            />
          ) : (
            <div className="h-full w-40 bg-muted rounded flex items-center justify-center text-muted-foreground text-sm">
              No Logo
            </div>
          )}
        </div>

        {/* Title Section */}
        <div
          ref={titleContainerRef}
          className="flex-1 flex items-center justify-center px-8"
        >
          <h1
            className={`font-bold font-hvh text-center transition-opacity ${
              !isPresentationMode ? "cursor-pointer hover:opacity-90" : ""
            }`}
            style={{
              color: "#FCD34D", // Golden color like S+ tier
              fontSize: `${titleFontSize}px`,
              lineHeight: 1.2,
            }}
            onClick={isPresentationMode ? undefined : handleTitleClick}
            title={isPresentationMode ? undefined : "Double-click to edit"}
          >
            {tierList.name}
          </h1>
        </div>

        {/* Presentation and Settings Section */}
        {!isPresentationMode && (
          <div className="shrink-0 flex gap-2">
            <Button
              variant="outline"
              size="icon"
              onClick={onTogglePresentationMode}
              title="Enter presentation mode (Cmd/Ctrl+P)"
            >
              <Presentation className="w-5 h-5" />
            </Button>
            <DropdownMenu
              open={isManageOpen}
              onOpenChange={(open) => {
                setIsManageOpen(open);
                if (!open) setExportError(""); // Clear error when closing
              }}
            >
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon">
                  <Settings className="w-5 h-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80">
                <div className="px-2 py-1.5 text-sm font-semibold">
                  Tier Lists
                </div>
                <DropdownMenuSeparator />

                <div className="max-h-96 overflow-y-auto">
                  {allTierLists.map((list) => (
                    <div
                      key={list.id}
                      className="flex items-center gap-1 px-2 py-1.5 hover:bg-accent hover:text-accent-foreground rounded-sm group"
                    >
                      <button
                        onClick={() => handleSwitchTierList(list.id)}
                        className="flex-1 text-left text-sm truncate min-w-0 px-1 py-0.5 rounded hover:bg-accent-foreground/10"
                        title={list.name}
                        aria-label={`Switch to ${list.name}`}
                      >
                        {list.name}
                        {list.id === tierList.id && (
                          <span className="ml-2 text-xs opacity-70">
                            (Current)
                          </span>
                        )}
                      </button>
                      <button
                        className="p-1 rounded hover:bg-accent-foreground/10 opacity-0 group-hover:opacity-100 transition-opacity text-accent-foreground"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditTierList(list.id);
                        }}
                        aria-label={`Edit ${list.name}`}
                        title="Edit"
                      >
                        <Pencil className="w-3 h-3" />
                      </button>
                      <button
                        className="p-1 rounded hover:bg-destructive/10 hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50 text-accent-foreground"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteTierList(list.id);
                        }}
                        disabled={deletingId === list.id}
                        aria-label={`Delete ${list.name}`}
                        title="Delete"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>

                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => {
                    setIsManageOpen(false);
                    setIsCreateOpen(true);
                  }}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create New Tier List
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleExportTemplate}>
                  <Download className="w-4 h-4 mr-2" />
                  Export as Template
                </DropdownMenuItem>
                {exportError && (
                  <div className="px-2 py-2">
                    <p className="text-sm text-destructive">{exportError}</p>
                  </div>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </div>

      {/* Edit Title Dialog */}
      <Dialog open={isEditTitleOpen} onOpenChange={setIsEditTitleOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Tier List Title</DialogTitle>
            <DialogDescription>
              Enter a new title for this tier list (max {MAX_TITLE_LENGTH}{" "}
              characters)
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <Input
              type="text"
              value={titleEditValue}
              onChange={(e) => {
                setTitleEditValue(e.target.value);
                setTitleError("");
              }}
              onKeyDown={handleTitleKeyDown}
              maxLength={MAX_TITLE_LENGTH}
              placeholder="Enter tier list title"
              className={titleError ? "border-red-500" : ""}
              autoFocus
            />
            {titleError && <p className="text-sm text-red-500">{titleError}</p>}
            <p className="text-xs text-muted-foreground">
              {titleEditValue.length}/{MAX_TITLE_LENGTH} characters
            </p>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsEditTitleOpen(false)}
            >
              Cancel
            </Button>
            <Button type="button" onClick={handleTitleSave}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create New Tier List Modal */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <TierListCreationWizard
            onSuccess={handleCreateSuccess}
            onCancel={() => setIsCreateOpen(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Edit Tier List Modal */}
      {editingTierListId &&
        (() => {
          const tierListToEdit = allTierLists.find(
            (list) => list.id === editingTierListId
          );
          return tierListToEdit ? (
            <Dialog
              open={!!editingTierListId}
              onOpenChange={(open) => !open && setEditingTierListId(null)}
            >
              <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Edit Tier List</DialogTitle>
                  <DialogDescription>
                    Update your tier list configuration
                  </DialogDescription>
                </DialogHeader>
                <EditTierListForm
                  tierList={tierListToEdit}
                  onSuccess={handleEditSuccess}
                  onCancel={() => setEditingTierListId(null)}
                />
              </DialogContent>
            </Dialog>
          ) : null;
        })()}

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Tier List</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete &quot;{tierListToDelete?.name}
              &quot;? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {deleteError && (
            <p className="text-sm text-destructive">{deleteError}</p>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setDeleteConfirmOpen(false);
                setTierListToDelete(null);
                setDeleteError("");
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={confirmDelete}
              disabled={!!deletingId}
            >
              {deletingId ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
